/**
 * Lead-forwarder health panel — Owner-only diagnostic surfaced on the
 * Tenants page. Calls /api/admin/lead-forwarder/health and renders:
 *   - Auth0 M2M token exchange status
 *   - Ekselens intake URL reachability
 *   - The intake URL + the last 6 chars of the M2M client_id (so the
 *     Owner can confirm which client is wired without exposing it)
 *
 * Loads on mount; refresh button re-probes on demand.
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const Dot = ({ ok, label }) => (
  <span
    className="inline-flex items-center gap-1.5 text-xs font-medium"
    data-testid={`lf-health-${label.toLowerCase().replace(/\s+/g, "-")}-dot`}
  >
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${
        ok === null
          ? "bg-slate-300"
          : ok
          ? "bg-emerald-500"
          : "bg-rose-500"
      }`}
    />
    <span className="text-ek-ink/70">{label}</span>
  </span>
);

export function LeadForwarderHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const probe = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/admin/lead-forwarder/health");
      setData(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Probe failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    probe();
  }, []);

  const overall =
    data && data.enabled && data.auth0_ok && data.ekselens_reachable;

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5"
      data-testid="lead-forwarder-health-panel"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            Ekselens lead-forwarder
          </h2>
          <p className="mt-1 text-xs text-ek-ink/60">
            Live wiring check for the Auth0 M2M → Ekselens Hub forward.
            Run any time the Owner needs to confirm leads are flowing into
            the shared Hub.
          </p>
        </div>
        <button
          type="button"
          onClick={probe}
          disabled={loading}
          className="rounded-lg border border-ek-purple/30 px-3 py-1.5 text-xs font-semibold text-ek-purple hover:bg-ek-purple/5 disabled:opacity-50"
          data-testid="lf-health-refresh-btn"
        >
          {loading ? "Probing…" : "Re-probe"}
        </button>
      </header>

      {err && (
        <p
          className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700"
          data-testid="lf-health-error"
        >
          {err}
        </p>
      )}

      {data && (
        <>
          <div
            className={`mt-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${
              overall
                ? "bg-emerald-50 text-emerald-800"
                : "bg-amber-50 text-amber-800"
            }`}
            data-testid="lf-health-overall"
          >
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${
                overall ? "bg-emerald-500" : "bg-amber-500"
              }`}
            />
            {overall
              ? "Forwarder healthy — leads are flowing to the Ekselens Hub."
              : "Forwarder degraded — see details below."}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-4">
            <Dot ok={data.enabled} label="Env vars" />
            <Dot ok={data.auth0_ok} label="Auth0 token" />
            <Dot ok={data.ekselens_reachable} label="Ekselens reachable" />
          </div>

          <dl className="mt-4 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
            <div>
              <dt className="text-ek-ink/50">Intake URL</dt>
              <dd
                className="font-mono text-ek-ink/80 break-all"
                data-testid="lf-health-intake-url"
              >
                {data.intake_url || "(not set)"}
              </dd>
            </div>
            <div>
              <dt className="text-ek-ink/50">Audience</dt>
              <dd className="font-mono text-ek-ink/80">
                {data.audience || "(not set)"}
              </dd>
            </div>
            <div>
              <dt className="text-ek-ink/50">M2M client (suffix)</dt>
              <dd
                className="font-mono text-ek-ink/80"
                data-testid="lf-health-client-suffix"
              >
                {data.client_id_suffix
                  ? `…${data.client_id_suffix}`
                  : "(not set)"}
              </dd>
            </div>
            <div>
              <dt className="text-ek-ink/50">Scope</dt>
              <dd className="font-mono text-ek-ink/80">
                {data.scopes || "(none)"}
              </dd>
            </div>
            {data.ekselens_status_code !== undefined && (
              <div>
                <dt className="text-ek-ink/50">Ekselens HEAD status</dt>
                <dd
                  className="font-mono text-ek-ink/80"
                  data-testid="lf-health-ekselens-status"
                >
                  {data.ekselens_status_code}
                </dd>
              </div>
            )}
          </dl>

          {data.errors && data.errors.length > 0 && (
            <ul
              className="mt-4 space-y-1 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700"
              data-testid="lf-health-error-list"
            >
              {data.errors.map((e, i) => (
                <li key={i}>• {e}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
